import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { MdAddCircleOutline, MdOutlineEdit } from 'react-icons/md'
import ReactSelect from 'react-select'

import useFollowUpConfigComprador from '@hook/followUpFornecedor/use-config-comprador'

import FuModalHeader from '@component/FollowUpFornecedor/ModalHeader'

import { IConfigCompradorItem } from '@base/@types/providers/FollowUpFornecedor/ConfigCompradorProvider'

type SelectOption = { value: string | number; label: string }

interface IForm {
  fornecedor: { value: number; label: string } | null
  categorias: SelectOption[]
  usuarios: SelectOption[]
}

const EMPTY_FORM: IForm = {
  fornecedor: null,
  categorias: [],
  usuarios: []
}

interface Props {
  isOpen: boolean
  onClose: () => void
  editItem: IConfigCompradorItem | null
  supplierOptions: { value: number; label: string }[]
  categoryOptions: { value: number; label: string }[]
  userOptions: SelectOption[]
  loadingFilters: boolean
}

const ManutencaoModal: FC<Props> = ({
  isOpen,
  onClose,
  editItem,
  supplierOptions,
  categoryOptions,
  userOptions,
  loadingFilters
}) => {
  const { create, update } = useFollowUpConfigComprador()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<IForm>(EMPTY_FORM)
  const isEdit = !!editItem

  useEffect(() => {
    if (!isOpen) return
    if (editItem) {
      const supplier = supplierOptions.find(
        s => s.value === editItem.fornecedorId
      ) ?? { value: editItem.fornecedorId, label: editItem.fornecedorNome }
      const selectedCategoria: SelectOption[] = editItem.categoriaCompactaId
        ? [
            categoryOptions.find(
              c => c.value === editItem.categoriaCompactaId
            ) ?? {
              value: editItem.categoriaCompactaId!,
              label:
                editItem.categoriaCompactaNome ??
                `ID ${editItem.categoriaCompactaId}`
            }
          ]
        : []
      const selectedUsers = editItem.usuarios
        .map(email => userOptions.find(u => u.label.trim() === email.trim()))
        .filter(Boolean) as SelectOption[]
      setForm({
        fornecedor: supplier,
        categorias: selectedCategoria,
        usuarios: selectedUsers
      })
    } else {
      setForm(EMPTY_FORM)
    }
  }, [isOpen, editItem, supplierOptions, categoryOptions, userOptions])

  const handleSave = async () => {
    if (!form.fornecedor) return
    if (form.usuarios.length === 0) return
    setSaving(true)
    try {
      const payload = {
        fornecedorId: form.fornecedor.value,
        categoriaCompactaIds:
          form.categorias.length === 0
            ? [null]
            : form.categorias.map(c => Number(c.value)),
        usuarios: form.usuarios.map(opt => String(opt.label))
      }
      if (isEdit) {
        await update(editItem!.id, payload)
      } else {
        await create(payload)
      }
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const isValid = !!form.fornecedor && form.usuarios.length > 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <FuModalHeader
          icon={isEdit ? MdOutlineEdit : MdAddCircleOutline}
          title={
            isEdit ? 'Editar Configuração' : 'Nova Configuração de Comprador'
          }
        />
        <ModalCloseButton color="white" />

        <ModalBody bg="white" py={5}>
          <FormControl mb={4} isRequired>
            <FormLabel fontSize="sm">Fornecedor</FormLabel>
            <ReactSelect
              isLoading={loadingFilters}
              options={supplierOptions}
              value={form.fornecedor}
              placeholder="Selecionar fornecedor..."
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
              onChange={opt => setForm(f => ({ ...f, fornecedor: opt as any }))}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm">
              Categoria Compacta
              <Text
                as="span"
                fontSize="11px"
                color="gray.500"
                ml={2}
                fontWeight="normal"
              >
                (opcional — vazio = todos os produtos)
              </Text>
            </FormLabel>
            <ReactSelect
              isMulti
              isLoading={loadingFilters}
              options={categoryOptions}
              value={form.categorias}
              placeholder="Todas as categorias do fornecedor..."
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
              onChange={opts =>
                setForm(f => ({
                  ...f,
                  categorias: (opts as SelectOption[]) ?? []
                }))
              }
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Usuários Responsáveis</FormLabel>
            <ReactSelect
              isMulti
              isLoading={loadingFilters}
              options={userOptions}
              value={form.usuarios}
              placeholder="Selecionar usuários..."
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
              onChange={opts =>
                setForm(f => ({
                  ...f,
                  usuarios: (opts as SelectOption[]) ?? []
                }))
              }
            />
          </FormControl>
        </ModalBody>

        <ModalFooter
          bg="white"
          borderBottomLeftRadius={5}
          borderBottomRightRadius={5}
        >
          <Button
            colorScheme="blue"
            mr={3}
            onClick={onClose}
            isDisabled={saving}
          >
            Cancelar
          </Button>
          <Button
            bg="green.600"
            color="white"
            _hover={{ bg: 'green.500' }}
            _active={{ bg: 'green.500' }}
            isLoading={saving}
            loadingText="Salvando..."
            isDisabled={!isValid}
            onClick={handleSave}
          >
            {isEdit ? 'Salvar Alterações' : 'Criar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ManutencaoModal
